import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchCustomers,
  createCustomer,
  updateCustomer,
  deleteCustomer,
} from '../redux/slices/customersSlice';
import { Modal, ConfirmDialog, LoadingSpinner, EmptyState } from '../components/Common';
import { useSearch } from '../context/SearchContext';
import { useEntityDetails } from '../context/EntityDetailsContext';
import { useLanguage } from '../context/LanguageContext';
import { printTable } from '../utils/printTable';

const emptyForm = { name: '', email: '', tel: '', street: '', house_number: '', zip: '', city: '' };

const Customers = () => {
  const dispatch = useDispatch();
  const { searchTerm } = useSearch();
  const { openCustomer } = useEntityDetails();
  const { t } = useLanguage();
  const { items: customers, loading, error } = useSelector((state) => state.customers);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    dispatch(fetchCustomers());
  }, [dispatch]);

  const filtered = customers.filter((c) => {
    const term = searchTerm.toLowerCase();
    return (
      c.name?.toLowerCase().includes(term) ||
      c.email?.toLowerCase().includes(term) ||
      c.city?.toLowerCase().includes(term) ||
      c.tel?.toLowerCase().includes(term)
    );
  });

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (customer) => {
    setEditingId(customer.id);
    setFormData({
      name: customer.name || '',
      email: customer.email || '',
      tel: customer.tel || '',
      street: customer.street || '',
      house_number: customer.house_number || '',
      zip: customer.zip || '',
      city: customer.city || '',
    });
    setShowModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const action = editingId
      ? updateCustomer({ id: editingId, data: formData })
      : createCustomer(formData);
    dispatch(action)
      .unwrap()
      .then(() => setShowModal(false))
      .catch((err) => console.error('Failed to save customer:', err));
  };

  const handleDelete = () => {
    dispatch(deleteCustomer(deleteTarget.id))
      .unwrap()
      .then(() => setDeleteTarget(null))
      .catch((err) => console.error('Failed to delete customer:', err));
  };

  if (error) {
    return (
      <div className='card bg-red-50 border border-red-200'>
        <p className='text-red-600'>{t('customers.errorLoading', { error })}</p>
      </div>
    );
  }

  const handlePrint = () => {
    printTable({
      title: t('customers.title'),
      columns: [
        { header: t('common.name') },
        { header: t('customers.email') },
        { header: t('customers.phone') },
        { header: t('customers.city') },
      ],
      rows: filtered.map((c) => [c.name, c.email, c.tel, c.city]),
    });
  };

  return (
    <div>
      <div className='flex justify-between items-center mb-6'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900'>{t('customers.title')}</h1>
          <p className='text-gray-600 mt-1'>{t('customers.subtitle')}</p>
        </div>
        <div className='flex items-center gap-3'>
          <button onClick={handlePrint} className='btn-secondary'>
            🖨️ {t('common.print')}
          </button>
          <button onClick={openCreate} className='btn-primary'>
            {t('customers.addCustomer')}
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon='🧑'
          title={t('customers.noCustomersTitle')}
          message={t('customers.noCustomersMessage')}
          actionLabel={t('customers.addCustomer')}
          onAction={openCreate}
          hasItems={customers.length > 0}
        />
      ) : (
        <div className='card overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('common.name')}</th>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('customers.email')}</th>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('customers.phone')}</th>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('customers.city')}</th>
                <th className='px-4 py-2 text-right text-xs font-medium text-gray-500'>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {filtered.map((c) => (
                <tr key={c.id} className='hover:bg-gray-50'>
                  <td
                    className='px-4 py-3 text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer'
                    onClick={() => openCustomer(c.id, c)}
                  >
                    {c.name}
                  </td>
                  <td className='px-4 py-3 text-sm text-gray-900'>{c.email || '-'}</td>
                  <td className='px-4 py-3 text-sm text-gray-900'>{c.tel || '-'}</td>
                  <td className='px-4 py-3 text-sm text-gray-900'>{c.city || '-'}</td>
                  <td className='px-4 py-3 text-right space-x-3'>
                    <button onClick={() => openCustomer(c.id, c)} className='text-sm text-blue-600 hover:text-blue-800'>
                      {t('common.view')}
                    </button>
                    <button onClick={() => openEdit(c)} className='text-sm text-blue-600 hover:text-blue-800'>
                      {t('common.edit')}
                    </button>
                    <button
                      onClick={() => setDeleteTarget(c)}
                      className='text-sm text-red-600 hover:text-red-800'
                    >
                      {t('common.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingId ? t('customers.editCustomer') : t('customers.createCustomer')}
        size='md'
      >
        <form onSubmit={handleSubmit}>
          <div className='space-y-4'>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>{t('common.name')} *</label>
              <input
                type='text'
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className='input-field'
                required
              />
            </div>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('customers.email')}</label>
                <input
                  type='email'
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className='input-field'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('customers.phone')}</label>
                <input
                  type='text'
                  value={formData.tel}
                  onChange={(e) => setFormData({ ...formData, tel: e.target.value })}
                  className='input-field'
                />
              </div>
            </div>
            <div className='grid grid-cols-3 gap-4'>
              <div className='col-span-2'>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('customers.street')}</label>
                <input
                  type='text'
                  value={formData.street}
                  onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                  className='input-field'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('customers.number')}</label>
                <input
                  type='text'
                  value={formData.house_number}
                  onChange={(e) => setFormData({ ...formData, house_number: e.target.value })}
                  className='input-field'
                />
              </div>
            </div>
            <div className='grid grid-cols-3 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('customers.zip')}</label>
                <input
                  type='text'
                  value={formData.zip}
                  onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                  className='input-field'
                />
              </div>
              <div className='col-span-2'>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('customers.city')}</label>
                <input
                  type='text'
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className='input-field'
                />
              </div>
            </div>
            <div className='flex justify-end space-x-3 pt-4'>
              <button type='button' onClick={() => setShowModal(false)} className='btn-secondary'>
                {t('common.cancel')}
              </button>
              <button type='submit' className='btn-primary'>
                {editingId ? t('customers.saveChanges') : t('customers.createCustomer')}
              </button>
            </div>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={t('customers.deleteCustomer')}
        message={t('customers.confirmDelete', { name: deleteTarget?.name })}
      />
    </div>
  );
};

export default Customers;
